import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from '@mui/material';

import { IUrlPattern } from 'reduxState/store/campaigns/types';
import { FormControl, FormHelperText, OutlinedInput } from '@mui/material';
import TextFieldInput from 'components/TextFieldInput/TextFieldInput';
import ValueTags from 'components/ValueTags/ValueTags';
import { processInputValue, createEnterKeyHandler } from '../utils';
import { Add as AddIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { isEmpty } from 'lodash';

const advancedTableHeaders = [
  { label: 'URL Pattern', field: 'pattern' },
  { label: 'Content Selector', field: 'contentSelector' },
  { label: 'Content Regex', field: 'contentRegex' },
  { label: 'Destination URL', field: 'destinationURL' },
];

interface ICampaignURLPatternsFieldProps {
  valuesArray: IUrlPattern[];
  hasError: boolean;
  urlPatternsError: string;
  hasAttemptedSubmit: boolean;
  disable?: boolean;
  handleAddUrlPattern: (value: IUrlPattern) => void;
  handleRemoveUrlPattern: (index: number) => void;
  handleUpdateUrlPattern: (index: number, value: IUrlPattern) => void;
  setUrlPatternsError?: (error: string) => void;
}

const CampaignURLPatternsField = ({
  valuesArray,
  hasError,
  urlPatternsError,
  hasAttemptedSubmit,
  disable = false,
  handleAddUrlPattern,
  handleRemoveUrlPattern,
  handleUpdateUrlPattern,
  setUrlPatternsError,
}: ICampaignURLPatternsFieldProps) => {
  const [value, setValue] = useState('');
  const [formMode, setFormMode] = useState<'single' | 'bulk' | 'advanced'>('single');
  const [advancedValues, setAdvancedValues] = useState<IUrlPattern[]>([
    { pattern: '', contentSelector: '', contentRegex: '', destinationURL: '' },
  ]);
  const [editableValues, setEditableValues] = useState<IUrlPattern[]>([]);

  // Check for any unsaved URL patterns in the advanced form
  const hasUnsavedUrlPatterns =
    advancedValues[0].pattern.trim() !== '' || editableValues.some(val => val !== undefined);

  useEffect(() => {
    const errorMessage = 'You have unsaved URL patterns';
    if (hasUnsavedUrlPatterns) {
      setUrlPatternsError?.(errorMessage);
    } else {
      setUrlPatternsError?.('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedValues, editableValues]);

  const handleOnAdd = (inputValue: string) => {
    if (formMode === 'advanced') return; // Advanced mode uses different input handling

    const addUrlPatternFromString = (pattern: string) => {
      handleAddUrlPattern({ pattern, contentSelector: '', contentRegex: '', destinationURL: '' });
    };
    processInputValue(inputValue, formMode as 'single' | 'bulk', addUrlPatternFromString, () => setValue(''));
  };

  const handleKeyDown = createEnterKeyHandler(() => handleOnAdd(value));

  const handleAdvancedValuesChange = (
    field: 'pattern' | 'contentSelector' | 'contentRegex' | 'destinationURL',
    value: string,
  ) => {
    const updatedValues = [...advancedValues];
    updatedValues[0] = { ...updatedValues[0], [field]: value };
    setAdvancedValues(updatedValues);
  };

  const handleEditableValueChange = (
    index: number,
    field: 'pattern' | 'contentSelector' | 'contentRegex' | 'destinationURL',
    value: string,
  ) => {
    const updatedValues = [...editableValues];
    const originalValue = valuesArray[index] || {
      pattern: '',
      contentSelector: '',
      contentRegex: '',
      destinationURL: '',
    };
    const currentEditableValue = updatedValues[index] || originalValue;

    // Update the field
    const newValue = { ...currentEditableValue, [field]: value };

    // Check if any field differs from the original
    const hasChanges =
      newValue.pattern !== originalValue.pattern ||
      newValue.contentSelector !== originalValue.contentSelector ||
      newValue.contentRegex !== originalValue.contentRegex ||
      newValue.destinationURL !== originalValue.destinationURL;

    if (hasChanges) {
      updatedValues[index] = newValue;
      setEditableValues(updatedValues);
    } else {
      // If no changes, remove from editableValues
      delete updatedValues[index];
      setEditableValues(updatedValues);
    }
  };

  const handleEditButtonClick = (index: number) => {
    // Get the current values from editableValues or fallback to valuesArray
    const updatedPattern = {
      pattern: editableValues[index]?.pattern ?? valuesArray[index]?.pattern ?? '',
      contentSelector: editableValues[index]?.contentSelector ?? valuesArray[index]?.contentSelector ?? '',
      contentRegex: editableValues[index]?.contentRegex ?? valuesArray[index]?.contentRegex ?? '',
      destinationURL: editableValues[index]?.destinationURL ?? valuesArray[index]?.destinationURL ?? '',
    };

    handleUpdateUrlPattern(index, updatedPattern);

    // Clear the editable values for this index after saving
    const updatedEditableValues = [...editableValues];
    delete updatedEditableValues[index];
    setEditableValues(updatedEditableValues);
  };

  const isRowBeingEdited = (index: number) => {
    return editableValues[index] !== undefined;
  };

  const renderAddButton = () => (
    <button
      type="button"
      className="px-4 py-2 font-bold text-white rounded-md bg-muted-dark-purple hover:bg-dark-purple"
      onClick={() => handleOnAdd(value)}
      data-testid={`add-url-pattern-button`}
      disabled={disable}
    >
      Add
    </button>
  );

  const renderSingleAddField = () => (
    <div>
      <FormControl className="flex gap-2 mb-2" error={hasError}>
        <div className="flex gap-2">
          <OutlinedInput
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
            placeholder="Enter URL pattern"
            data-testid="url-pattern-input"
            disabled={disable}
          />
          {!disable && renderAddButton()}
        </div>
        {hasError && <FormHelperText>Please add at least one valid URL pattern</FormHelperText>}
      </FormControl>
    </div>
  );

  const renderBulkAddField = () => (
    <div className="flex flex-col">
      <TextFieldInput
        label=""
        value={value}
        handleChange={setValue}
        hasError={hasError}
        placeholderText="Enter URL pattern"
        data-testid="url-patterns-input"
        rows={3}
        isDisabled={disable}
        onKeyDown={handleKeyDown}
        errorText={hasError ? 'Please add at least one valid URL pattern' : undefined}
      />
      <div className="flex justify-end">{renderAddButton()}</div>
    </div>
  );

  const handleAdvancedAdd = () => {
    if (!advancedValues[0].pattern.trim()) return;

    handleAddUrlPattern({
      pattern: advancedValues[0].pattern.trim(),
      contentSelector: advancedValues[0].contentSelector.trim(),
      contentRegex: advancedValues[0].contentRegex.trim(),
      destinationURL: advancedValues[0].destinationURL.trim(),
    });

    setAdvancedValues([{ pattern: '', contentSelector: '', contentRegex: '', destinationURL: '' }]);
  };

  const handleAdvancedKeyDown = createEnterKeyHandler(() => handleAdvancedAdd());

  const handleUpdateKeyDown = (index: number) => createEnterKeyHandler(() => handleEditButtonClick(index));

  const renderAdvancedTable = () => (
    <div className="flex flex-col gap-2">
      <div
        className={`border rounded border-solid border-red ${
          hasAttemptedSubmit && !isEmpty(urlPatternsError) ? '!border-[#d32f2f]' : '!border-gray-300'
        }`}
      >
        <Table className="w-full font-montserrat" size="small">
          <TableHead className="bg-[#ebebeb]">
            <TableRow>
              {advancedTableHeaders.map((header, index) => (
                <TableCell
                  key={index}
                  variant="head"
                  className="font-montserrat font-semibold text-center text-sm py-2 text-dark-purple"
                >
                  {header.label}
                </TableCell>
              ))}
              {!disable && (
                <TableCell
                  variant="head"
                  className="font-montserrat font-semibold text-center text-sm py-2 text-dark-purple"
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {valuesArray.map((_, index) => (
              <TableRow key={index}>
                <TableCell variant="body" className="text-center">
                  <TextField
                    className="[&>div>input]:text-center [&>div>input]:text-sm [&>div>input]:!py-2"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    value={editableValues[index]?.pattern ?? valuesArray[index]?.pattern ?? ''}
                    onChange={e => handleEditableValueChange(index, 'pattern', e.target.value)}
                    onKeyDown={handleUpdateKeyDown(index)}
                  />
                </TableCell>
                <TableCell variant="body" className="text-center">
                  <TextField
                    className="[&>div>input]:text-center [&>div>input]:text-sm [&>div>input]:!py-2"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    value={editableValues[index]?.contentSelector ?? valuesArray[index]?.contentSelector ?? ''}
                    onChange={e => handleEditableValueChange(index, 'contentSelector', e.target.value)}
                    onKeyDown={handleUpdateKeyDown(index)}
                  />
                </TableCell>
                <TableCell variant="body" className="text-center">
                  <TextField
                    className="[&>div>input]:text-center [&>div>input]:text-sm [&>div>input]:!py-2"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    value={editableValues[index]?.contentRegex ?? valuesArray[index]?.contentRegex ?? ''}
                    onChange={e => handleEditableValueChange(index, 'contentRegex', e.target.value)}
                    onKeyDown={handleUpdateKeyDown(index)}
                  />
                </TableCell>
                <TableCell variant="body" className="text-center">
                  <TextField
                    className="[&>div>input]:text-center [&>div>input]:text-sm [&>div>input]:!py-2"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    value={editableValues[index]?.destinationURL ?? valuesArray[index]?.destinationURL ?? ''}
                    onChange={e => handleEditableValueChange(index, 'destinationURL', e.target.value)}
                    onKeyDown={handleUpdateKeyDown(index)}
                  />
                </TableCell>
                {!disable && (
                  <TableCell variant="body" className="text-center">
                    {isRowBeingEdited(index) ? (
                      <button
                        type="button"
                        onClick={() => handleEditButtonClick(index)}
                        className="px-2 py-1 text-sm font-bold text-white rounded bg-muted-dark-purple hover:bg-dark-purple"
                      >
                        <EditIcon fontSize="small" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRemoveUrlPattern(index)}
                        className="px-2 py-1 text-sm font-bold text-white rounded bg-muted-dark-purple hover:bg-dark-purple"
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}

            {/* Input row */}
            {!disable && (
              <TableRow>
                <TableCell variant="body" className="text-center border-0">
                  <TextField
                    className="[&>div>input]:text-center [&>div>input]:text-sm [&>div>input]:!py-2"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    placeholder="Enter URL pattern*"
                    value={advancedValues[0].pattern}
                    onChange={e => handleAdvancedValuesChange('pattern', e.target.value)}
                    onKeyDown={handleAdvancedKeyDown}
                  />
                </TableCell>
                <TableCell variant="body" className="text-center border-0">
                  <TextField
                    className="[&>div>input]:text-center [&>div>input]:text-sm [&>div>input]:!py-2"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    placeholder="Enter content selector"
                    value={advancedValues[0].contentSelector}
                    onChange={e => handleAdvancedValuesChange('contentSelector', e.target.value)}
                    onKeyDown={handleAdvancedKeyDown}
                  />
                </TableCell>
                <TableCell variant="body" className="text-center border-0">
                  <TextField
                    className="[&>div>input]:text-center [&>div>input]:text-sm [&>div>input]:!py-2"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    placeholder="Enter content regex"
                    value={advancedValues[0].contentRegex}
                    onChange={e => handleAdvancedValuesChange('contentRegex', e.target.value)}
                    onKeyDown={handleAdvancedKeyDown}
                  />
                </TableCell>
                <TableCell variant="body" className="text-center border-0">
                  <TextField
                    className="[&>div>input]:text-center [&>div>input]:text-sm [&>div>input]:!py-2"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    placeholder="Enter destination URL"
                    value={advancedValues[0].destinationURL}
                    onChange={e => handleAdvancedValuesChange('destinationURL', e.target.value)}
                    onKeyDown={handleAdvancedKeyDown}
                  />
                </TableCell>
                <TableCell variant="body" className="text-center border-0">
                  <button
                    type="button"
                    className="px-2 py-1 font-bold text-white rounded bg-muted-dark-purple hover:bg-dark-purple disabled:bg-light-grey disabled:text-dark-grey"
                    onClick={handleAdvancedAdd}
                    disabled={!advancedValues[0].pattern.trim()}
                  >
                    <AddIcon fontSize="small" />
                  </button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {hasError ? (
        <FormHelperText className="text-[#d32f2f] mt-[3px] mx-[14px]">
          Please add at least one valid URL pattern
        </FormHelperText>
      ) : (
        hasAttemptedSubmit &&
        !isEmpty(urlPatternsError) && (
          <FormHelperText className="text-[#d32f2f] mt-[3px] mx-[14px]">{urlPatternsError}</FormHelperText>
        )
      )}
    </div>
  );

  const renderForm = () => {
    switch (formMode) {
      case 'single':
        return renderSingleAddField();
      case 'bulk':
        return renderBulkAddField();
      case 'advanced':
        return renderAdvancedTable();
    }
  };

  const handleToggleButtonChange = (value: 'single' | 'bulk' | 'advanced') => {
    setFormMode(value);
  };
  return (
    <div className="mb-4">
      <div className="flex items-center justify-start mb-4">
        <h5 className=" text-muted-dark-purple mr-4">URL Patterns</h5>
        {!disable && (
          <ToggleButtonGroup
            value={formMode}
            onChange={(_, value) => handleToggleButtonChange(value)}
            exclusive
            size="small"
          >
            <ToggleButton value="single" className="normal-case font-montserrat text-xs py-1">
              Single
            </ToggleButton>
            <ToggleButton value="bulk" className="normal-case font-montserrat text-xs py-1">
              Bulk
            </ToggleButton>
            <ToggleButton value="advanced" className="normal-case font-montserrat text-xs py-1">
              Advanced
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </div>

      {renderForm()}
      {valuesArray.length > 0 && formMode !== 'advanced' && (
        <ValueTags
          valueArray={valuesArray}
          handleRemoveValue={handleRemoveUrlPattern}
          disable={disable}
          getDisplayText={(value: IUrlPattern) => value.pattern}
        />
      )}
    </div>
  );
};

export default CampaignURLPatternsField;
